import { useEffect, useRef, useState, type TextareaHTMLAttributes } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Select, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import {
  getSitePageOutline, suggestSitePageOutline, saveSitePageOutline,
  type OutlineState, type PageOutline, type OutlineTopic, type SitePage,
} from '@/data/client';

/* A page's outline (docs/bulk-write-plan.md, Phase E): the topics the Vault
   supports for this page, how much evidence is behind each, and what to show
   it with. The Vault suggests it; Triton edits it; Claude Code lays out the
   scaffold from it; the writer reads which material belongs in which
   section. Topics and evidence only — the copy comes from the Vault. */

const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);
const STRENGTHS = [{ value: 'strong', label: 'Strong — its own section' }, { value: 'some', label: 'Some — short section or list' }, { value: 'thin', label: 'Thin — fold in or leave out' }];
const ROLES = [{ value: 'experience', label: 'Experience' }, { value: 'expertise', label: 'Expertise' }, { value: 'trust', label: 'Trust' }, { value: 'answer', label: 'Answer' }];
const LENGTHS = [{ value: 'short', label: 'Short' }, { value: 'standard', label: 'Standard' }, { value: 'long', label: 'Long' }];
const blankTopic = (): OutlineTopic => ({ topic: '', strength: 'some', role: 'expertise', component: '', placeholder: '', evidence: '' });

/* Every text in an outline is a sentence or two, and a one-line box hid all
   but the first few words of each — the outline could not be read, let alone
   judged, before saving. A box that grows with its content, one line when
   empty. */
function Grow({ value, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; hint?: string; value: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return <Textarea ref={ref} rows={1} className="ol__grow" value={value} {...rest} />;
}

export function PageOutlineEditor({ page, busy, onNote, onErr }: { page: SitePage; busy: boolean; onNote: (m: string) => void; onErr: (m: string) => void }) {
  const qc = useQueryClient();
  const key = ['siteMap', 'outline', page.id];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => getSitePageOutline(page.id) });
  const [draft, setDraft] = useState<PageOutline | null>(null);
  const [emphasis, setEmphasis] = useState('');
  const [working, setWorking] = useState<'suggest' | 'save' | null>(null);
  useEffect(() => { setDraft(data?.outline ?? null); setEmphasis(data?.outline?.emphasis ?? ''); }, [data]);

  const put = (s: OutlineState) => { qc.setQueryData(key, s); void qc.invalidateQueries({ queryKey: ['siteMap', 'writeBoard'] }); };
  const act = async (label: 'suggest' | 'save', fn: () => Promise<OutlineState>, note: string) => {
    setWorking(label); onErr('');
    try { put(await fn()); onNote(note); } catch (e) { onErr(errMsg(e, 'That did not work.')); } finally { setWorking(null); }
  };
  const suggest = () => {
    if (data?.edited && !confirm('Replace this outline with a fresh suggestion from the Vault? Your edits to the topics are lost; the emphasis is kept.')) return;
    void act('suggest', () => suggestSitePageOutline(page.id, emphasis), `Outline suggested for ${page.title || page.route}. Review it and save.`);
  };
  const setTopic = (i: number, patch: Partial<OutlineTopic>) => setDraft((d) => d && { ...d, topics: d.topics.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const move = (i: number, by: number) => setDraft((d) => {
    if (!d || i + by < 0 || i + by >= d.topics.length) return d;
    const topics = [...d.topics];
    [topics[i], topics[i + by]] = [topics[i + by], topics[i]];
    return { ...d, topics };
  });

  if (isLoading) return <p className="dash-muted smb__meta">Loading the outline…</p>;
  if (!data) return null;
  const status = !data.outline ? 'No outline yet'
    : `${data.edited ? 'Edited' : 'Suggested'} ${data.at ? data.at.slice(0, 16).replace('T', ' ') : ''}${data.stale ? ' — the Vault has changed since; suggest again to catch new material' : ''}`;
  const dirty = JSON.stringify(draft) !== JSON.stringify(data.outline) || (draft != null && emphasis !== (data.outline?.emphasis ?? ''));

  return (
    <div className="smb__questions ol">
      <h5>Outline <span className="dash-muted"> · shapes the scaffold; the Vault writes the copy</span></h5>
      <p className="dash-muted smb__meta" style={{ marginTop: 0 }}>{status}</p>
      <Textarea label="Triton's emphasis" rows={2} value={emphasis} onChange={(e) => setEmphasis(e.target.value)} placeholder="Optional: what this page should lead with or highlight, e.g. lead with tankless conversions; keep financing light." />
      <div className="smb__formactions" style={{ marginTop: 8, marginBottom: 8 }}>
        <Button variant="outline" size="sm" disabled={busy || !!working} onClick={suggest} iconLeft={<Icon name="sparkles" size={13} />}>
          {working === 'suggest' ? 'Reading the Vault…' : data.outline ? 'Suggest again' : 'Suggest an outline'}
        </Button>
        {!draft && <Button variant="ghost" size="sm" disabled={busy || !!working} onClick={() => setDraft({ aboveFold: { angle: '', cta: '' }, topics: [blankTopic()], closingCta: '', length: 'standard', emphasis: '' })}>Write one by hand</Button>}
      </div>

      {draft && (
        <div className="ol__body">
          <div className="ol__section">
            <h6 className="ol__h">Above the fold <span className="dash-muted">· the site's layout stays; only these two change</span></h6>
            <Grow label="Headline angle" value={draft.aboveFold.angle} onChange={(e) => setDraft({ ...draft, aboveFold: { ...draft.aboveFold, angle: e.target.value } })} />
            <Grow label="Primary call to action" value={draft.aboveFold.cta} onChange={(e) => setDraft({ ...draft, aboveFold: { ...draft.aboveFold, cta: e.target.value } })} />
          </div>
          <h6 className="ol__h">Sections, in order <span className="dash-muted">· one per topic; a thin topic folds into prose</span></h6>
          <ol className="ol__topics">
            {draft.topics.map((t, i) => (
              <li key={i} className={`ol__topic ol__topic--${t.strength}`}>
                <div className="ol__topicrow">
                  <span className="ol__num">{i + 1}</span>
                  <Grow label="Topic" value={t.topic} onChange={(e) => setTopic(i, { topic: e.target.value })} placeholder="What this section is about — a topic, never a fact" />
                  <div className="ol__tools">
                    <button type="button" className="smb__ibtn" title="Move up" disabled={i === 0} onClick={() => move(i, -1)}><Icon name="chevron-down" size={13} style={{ transform: 'rotate(180deg)' }} /></button>
                    <button type="button" className="smb__ibtn" title="Move down" disabled={i === draft.topics.length - 1} onClick={() => move(i, 1)}><Icon name="chevron-down" size={13} /></button>
                    <button type="button" className="smb__ibtn" title="Remove this topic" onClick={() => setDraft({ ...draft, topics: draft.topics.filter((_, j) => j !== i) })}><Icon name="x" size={12} /></button>
                  </div>
                </div>
                <div className="ol__meta">
                  <Select label="Evidence" value={t.strength} onChange={(e) => setTopic(i, { strength: e.target.value as OutlineTopic['strength'] })} options={STRENGTHS} />
                  <Select label="Role" value={t.role} onChange={(e) => setTopic(i, { role: e.target.value as OutlineTopic['role'] })} options={ROLES} />
                </div>
                <Grow label="Show it as" value={t.component} onChange={(e) => setTopic(i, { component: e.target.value })} placeholder="process steps, bullet list, photo with caption…" />
                <Grow label="Placeholder rule" value={t.placeholder} onChange={(e) => setTopic(i, { placeholder: e.target.value })} placeholder="H2 showcasing …" hint="How the scaffold labels this section for the writer." />
                <Grow label="Where the material is" value={t.evidence} onChange={(e) => setTopic(i, { evidence: e.target.value })} placeholder="source titles, 4 job photos, 2 reviews — never the facts themselves" />
              </li>
            ))}
          </ol>
          <button type="button" className="smb__link" onClick={() => setDraft({ ...draft, topics: [...draft.topics, blankTopic()] })}>Add a topic</button>
          <div className="ol__section">
            <h6 className="ol__h">Closing</h6>
            <Grow label="Closing call to action" value={draft.closingCta} onChange={(e) => setDraft({ ...draft, closingCta: e.target.value })} />
            <Select label="Length" value={draft.length} onChange={(e) => setDraft({ ...draft, length: e.target.value as PageOutline['length'] })} options={LENGTHS} className="ol__length" />
          </div>
          <div className="smb__formactions" style={{ marginTop: 8 }}>
            {data.outline && <Button variant="ghost" size="sm" disabled={busy || !!working} onClick={() => { if (confirm('Remove this page\'s outline?')) void act('save', () => saveSitePageOutline(page.id, null), 'Outline removed.'); }}>Remove outline</Button>}
            <span style={{ flex: 1 }} />
            {dirty && <Button variant="ghost" size="sm" disabled={!!working} onClick={() => { setDraft(data.outline); setEmphasis(data.outline?.emphasis ?? ''); }}>Discard changes</Button>}
            <Button variant="primary" size="sm" disabled={busy || !!working || !dirty || !draft.topics.some((t) => t.topic.trim())} onClick={() => act('save', () => saveSitePageOutline(page.id, { ...draft, emphasis }), 'Outline saved. Copy the Claude Code prompt to scaffold from it.')}>
              {working === 'save' ? 'Saving…' : 'Save outline'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
